import React, { useState } from "react";
import { FaFileAlt, FaFolder, FaFolderOpen, FaLock } from "react-icons/fa";
import data from "../content/launchpadContentMap.json";
import { useTheme } from "../contexts/ThemeContext";
 
const Sidebar = ({ onSelect, selectedPath, isPremium = false }) => {
  const [activeFile, setActiveFile] = useState(null);
  const [expanded, setExpanded] = useState({});
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Counter to keep track of global file index for "first 2 free" logic
  let globalFileIndex = 0;

  // Auto-open parent folders if a file is selected (e.g. on load)
  React.useEffect(() => {
    if (selectedPath && data) {
      const newExpanded = { ...expanded };
      let found = false;

      const findAndExpand = (nodes, path = "") => {
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i];
          const key = `${path}/${node.label}-${i}`;
          
          if (node.files && node.files.some(f => f.file === selectedPath)) {
            newExpanded[key] = true;
            found = true;
            return true;
          }
          
          if (node.children && findAndExpand(node.children, key)) {
            newExpanded[key] = true;
            return true;
          }
        }
        return false;
      };

      findAndExpand(data);
      if (found) {
        setExpanded(newExpanded);
      }
    }
  }, [selectedPath, data]);

  const toggleExpand = (key) => {
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getFolderColor = () => "var(--secondary-purple)"; // purple for all folders

  const renderTree = (nodes = [], level = 0, path = "") =>
    nodes
      .filter(Boolean)
      .map((node, idx) => {
        const key = `${path}/${node.label}-${idx}`;
        const isExpanded = expanded[key];
        const label = node.label.replaceAll("_", " ");

        if (node.children) {
          return (
            <div key={key} className={`ml-${level * 2}`}>
              <div
                onClick={() => toggleExpand(key)}
                className="cursor-pointer flex items-center gap-2 font-black text-xs py-2 uppercase tracking-widest hover:text-primary transition-colors"
                style={{ color: getFolderColor() }}
              >
                {isExpanded ? <FaFolderOpen size={13} /> : <FaFolder size={13} />}
                {label}
              </div>
              {isExpanded && (
                <div className="ml-4">{renderTree(node.children, level + 1, key)}</div>
              )}
            </div>
          );
        }

        if (node.files) {
          return (
            <div key={key} className={`ml-${level * 2}`}>
              <div
                onClick={() => toggleExpand(key)}
                className="cursor-pointer flex items-center gap-2 font-black text-xs py-2 uppercase tracking-widest hover:text-primary transition-colors"
                style={{ color: getFolderColor() }}
              >
                {isExpanded ? <FaFolderOpen size={13} /> : <FaFolder size={13} />}
                {label}
              </div>
              {isExpanded && (
                <div className="ml-4 space-y-1">
                  {node.files.map((file, i) => {
                    const isSelected = activeFile === file.file;
                    const currentIndex = globalFileIndex;
                    
                    // New Rule: Level 1-5 free. 
                    // Path example: .../Level 1/...
                    const isFreeLevel = (path) => {
                      if (!path) return false;
                      const match = path.match(/level[- ]?(\d+)/i);
                      if (match) {
                        const levelNum = parseInt(match[1]);
                        return levelNum >= 1 && levelNum <= 5;
                      }
                      // Fallback: If no level mentioned, first 2 are free
                      return currentIndex < 2; 
                    };

                    const isLocked = !isPremium && !isFreeLevel(file.file);

                    // Increment global file counter
                    globalFileIndex++;

                    return (
                      <div
                        key={i}
                        onClick={() => onSelect(file.file, isLocked, currentIndex)}
                        className={`flex items-center justify-between gap-2 text-sm cursor-pointer px-3 py-2 rounded-xl transition-all duration-200 ${
                          isSelected
                            ? "bg-primary/20 text-primary font-bold shadow-sm"
                            : "text-body dark:text-gray-300 hover:bg-primary/5 hover:text-primary"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <FaFileAlt size={12} className={isSelected ? "text-primary" : "text-muted"} />
                          {file.title.replaceAll("_", " ")}
                        </div>
                        {isLocked && <FaLock size={10} className="text-gray-400 opacity-60" />}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        return null;
      });
 
  return (
    <div className={`h-full px-6 pb-6 w-full overflow-y-auto max-h-screen transition-all duration-300 relative custom-scrollbar bg-transparent ${
      isDark ? "text-white" : "text-body"
    }`}>
      <div className={`sticky top-0 z-20 ${isDark ? "bg-bgMain" : "bg-white"} pt-6`}>
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-primary opacity-50"></div>
        <h2 className="text-xl font-extrabold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent flex items-center gap-2 uppercase tracking-tighter py-4">
          📘 Docs
        </h2>
      </div>
      <div className="space-y-1">
        {renderTree(data)}
      </div>

      {/* Branding Footer */}
      <div className="mt-auto pt-10 pb-4 border-t border-primary/10">
        <div 
          className="flex flex-col items-center justify-center cursor-pointer group"
          onClick={() => window.open("https://www.theskillgenie.com", "_blank")}
        >
          <span className="text-[10px] font-bold text-muted uppercase tracking-[0.2em] mb-1">Powered by</span>
          <div className="flex items-center gap-1.5">
            <span className={`text-xs font-black bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent uppercase tracking-tight`}>
              theskillgenie
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
 
export default Sidebar;



